import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-8 h-8 text-accent-blue" />
        <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
      </div>

      <div className="prose prose-invert max-w-none space-y-8">
        <p className="text-gray-400">
          <strong>Effective Date:</strong> January 1, 2025<br />
          <strong>Last Updated:</strong> January 26, 2025
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white">1. Introduction</h2>
          <p className="text-gray-300">
            Northcoast AI Academy ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use our educational platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">2. Information We Collect</h2>

          <h3 className="text-lg font-medium text-white mt-4">2.1 Information You Provide</h3>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li><strong>Account Information:</strong> Name, email address, password</li>
            <li><strong>Profile Information:</strong> Learning preferences, accessibility settings</li>
            <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store card details)</li>
            <li><strong>Learning Content:</strong> Reflections, quiz answers, AI interactions</li>
            <li><strong>Communications:</strong> Support requests, feedback</li>
          </ul>

          <h3 className="text-lg font-medium text-white mt-4">2.2 Information Collected Automatically</h3>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li><strong>Usage Data:</strong> Pages visited, lessons completed, time spent, features used</li>
            <li><strong>Device Information:</strong> Browser type, operating system, screen resolution</li>
            <li><strong>Log Data:</strong> IP address, access times, referral URLs</li>
            <li><strong>Cookies:</strong> Session management and preferences (see Cookie Policy)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">3. How We Use Your Information</h2>
          <p className="text-gray-300">We use collected information to:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Provide, maintain, and improve our educational services</li>
            <li>Personalize your learning experience</li>
            <li>Track your progress and award achievements</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send important updates about your account or the Service</li>
            <li>Respond to your inquiries and provide support</li>
            <li>Analyze usage patterns to improve our platform</li>
            <li>Detect and prevent fraud or abuse</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">4. AI Features and Data Processing</h2>
          <p className="text-gray-300">
            Our platform includes AI-powered educational features. When you use these features:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Your inputs may be sent to third-party AI providers (Anthropic, Google) for processing</li>
            <li>AI interactions may be logged to improve service quality</li>
            <li>We do not use your learning content to train AI models</li>
            <li>AI providers process data according to their respective privacy policies</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">5. Information Sharing</h2>
          <p className="text-gray-300">We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li><strong>Service Providers:</strong> Payment processors (Stripe), hosting (Cloudflare), AI providers</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Consent:</strong> When you explicitly authorize sharing</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">6. Data Retention</h2>
          <p className="text-gray-300">
            We retain your information for as long as your account is active or as needed to provide services.
            After account deletion:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Account data is deleted within 30 days</li>
            <li>Analytics data may be retained in anonymized form</li>
            <li>Legal and compliance records may be retained longer as required</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">7. Your Rights and Choices</h2>
          <p className="text-gray-300">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correct:</strong> Update inaccurate or incomplete information</li>
            <li><strong>Delete:</strong> Request deletion of your account and data</li>
            <li><strong>Export:</strong> Receive your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Restrict:</strong> Limit certain processing of your data</li>
          </ul>
          <p className="text-gray-300 mt-2">
            To exercise these rights, contact us at privacy@northcoast.ai
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">8. Data Security</h2>
          <p className="text-gray-300">
            We implement appropriate security measures including:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Encryption of data in transit (TLS/HTTPS)</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Regular security assessments</li>
            <li>Limited employee access to personal data</li>
            <li>Cloudflare DDoS protection and WAF</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">9. International Data Transfers</h2>
          <p className="text-gray-300">
            Your information may be transferred to and processed in countries outside your residence.
            We ensure appropriate safeguards are in place for international transfers.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">10. Children's Privacy</h2>
          <p className="text-gray-300">
            Our Service is not intended for children under 13. We do not knowingly collect personal information
            from children under 13. If you believe we have collected such information, please contact us immediately.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">11. California Privacy Rights (CCPA)</h2>
          <p className="text-gray-300">
            California residents have additional rights including:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Right to know what personal information is collected</li>
            <li>Right to know whether information is sold or disclosed</li>
            <li>Right to opt-out of the sale of personal information (we do not sell data)</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">12. Changes to This Policy</h2>
          <p className="text-gray-300">
            We may update this Privacy Policy periodically. We will notify you of material changes via email
            or through the Service. Continued use after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">13. Contact Us</h2>
          <p className="text-gray-300">
            For privacy-related questions or to exercise your rights:
          </p>
          <p className="text-gray-300 mt-2">
            <strong>Email:</strong> privacy@northcoast.ai<br />
            <strong>Address:</strong> Northcoast AI Academy, Cleveland, OH, USA
          </p>
        </section>
      </div>
    </div>
  );
}
